import { useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform, animate } from "framer-motion";
import { Link } from "react-router-dom";
import { IMG, resolveImg } from "../lib/api";

export const EASE = [0.22, 1, 0.36, 1];

/* Masked line-by-line text reveal */
export function MaskedLine({ children, delay = 0, className = "", as: Tag = "div" }) {
  return (
    <Tag className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
        data-testid="masked-line"
      >
        {children}
      </motion.div>
    </Tag>
  );
}

/* Scroll-triggered reveal wrapper */
export function Reveal({ children, delay = 0, y = 36, className = "", once = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
      data-testid="reveal"
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ eyebrow, title, italic, description, align = "left", grotesk = false }) {
  const center = align === "center";
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-3xl`}>
      {eyebrow && (
        <Reveal>
          <p className="eyebrow" style={center ? { textAlign: "center" } : {}}>
            {eyebrow}
          </p>
        </Reveal>
      )}
      {title && (
        <Reveal delay={0.08}>
          <h2 className={`mt-5 ${grotesk ? "font-grotesk tracking-tight" : "font-display"} text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight`}>
            <span className="text-silver">{title}</span>{" "}
            {italic && <em className="metallic font-medium">{italic}</em>}
          </h2>
        </Reveal>
      )}
      {description && (
        <Reveal delay={0.16}>
          <p className="mt-5 text-sm sm:text-base leading-relaxed text-white">{description}</p>
        </Reveal>
      )}
    </div>
  );
}

export function Tag({ children, tone = "silver" }) {
  const tones = {
    silver: "border-silver/30 text-silver-dim",
    ember: "border-ember/50 text-ember",
  };
  return (
    <span
      className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${tones[tone]}`}
      data-testid="tag"
    >
      {children}
    </span>
  );
}

/* Placeholder for club photographs not yet added */
export function PhotoFrame({ src, alt, label, className = "", imgClassName = "" }) {
  if (!src) {
    return (
      <div
        className={`corner-frame relative flex flex-col items-center justify-center gap-3 border border-silver/15 bg-navy/60 ${className}`}
        data-testid="photo-placeholder"
      >
        <svg width="34" height="34" viewBox="0 0 64 64" fill="none" aria-hidden>
          <circle cx="32" cy="32" r="30" stroke="#94A3B8" strokeWidth="2" opacity="0.6" />
          <path d="M32 15 C 39 24, 43 28, 43 36 A 11 11 0 0 1 21 36 C 21 28, 25 24, 32 15 Z" stroke="#94A3B8" strokeWidth="2" opacity="0.8" />
          <circle cx="32" cy="37" r="3" fill="#E2E8F0" opacity="0.9" />
        </svg>
        <p className="px-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
          {label || "Photograph coming soon"}
        </p>
      </div>
    );
  }
  return (
    <div className={`corner-frame overflow-hidden ${className}`} data-testid="photo-frame">
      <img src={resolveImg(src)} alt={alt || label || ""} loading="lazy" className={`h-full w-full object-cover ${imgClassName}`} />
    </div>
  );
}

/* Animated counter */
export function Counter({ value, suffix = "", duration = 1.8 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value, duration]);
  return (
    <span ref={ref} data-testid="counter-value">
      {display}
      {suffix}
    </span>
  );
}

/* Full-width parallax banner between sections */
export function BannerStrip({ image = IMG.forestMist, children, height = "py-28" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className={`relative overflow-hidden ${height}`} data-testid="banner-strip">
      <motion.img
        src={image}
        alt=""
        style={{ y }}
        className="absolute inset-0 h-[124%] w-full object-cover opacity-75"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />
      <div className="relative z-10">{children}</div>
    </section>
  );
}

/* Page hero for inner pages */
export function PageHero({ eyebrow, title, italic, image = IMG.forestMist, grotesk = false, children }) {
  return (
    <section className="relative flex min-h-[52vh] items-end overflow-hidden pb-16 pt-40" data-testid="page-hero">
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/25 to-ink" />
      </div>
      <div className="relative z-10 mx-auto w-full max-w-6xl px-6">
        <MaskedLine>
          <p className="eyebrow">{eyebrow}</p>
        </MaskedLine>
        <MaskedLine delay={0.12} className="pb-2 sm:pb-3">
          <h1 className={`mt-6 ${grotesk ? "font-grotesk tracking-tight" : "font-display"} text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl`}>
            <span className="text-silver">{title}</span> {italic && <em className="metallic font-medium">{italic}</em>}
          </h1>
        </MaskedLine>
        {children && (
          <Reveal delay={0.25} className="mt-6 max-w-2xl">
            {children}
          </Reveal>
        )}
      </div>
    </section>
  );
}

export function EmptyState({ title = "Nothing here yet", note }) {
  return (
    <div className="corner-frame flex flex-col items-center justify-center gap-3 border border-silver/15 bg-navy/40 px-8 py-16 text-center" data-testid="empty-state">
      <span className="h-2 w-2 rotate-45 bg-ember/80" />
      <p className="font-display text-2xl italic text-silver">{title}</p>
      {note && <p className="max-w-md text-sm text-silver-muted">{note}</p>}
    </div>
  );
}

export function GhostLink({ to, children }) {
  return (
    <Link to={to} className="btn-silver" data-testid={`link-${to.replace(/\//g, "-") || "home"}`}>
      <span>{children}</span>
    </Link>
  );
}
